import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { FaChevronDown, FaSearch, FaTimes, FaPlus, FaFilter, FaCheck, FaGripVertical, FaQuestionCircle } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import ConditionRow from '../common/ConditionRow';
import DateFilterLiteralsHelpModal from '../common/DateFilterLiteralsHelpModal';
import { isOperatorValueRequired } from '../../utils/conditionBuilderUtils';
import './css/FieldSelector.css';

/** Map list-view / ConditionRow operators to values stored on report filters (preview + run SQL). */
const fromConditionRowOperator = op => {
  if (!op) return 'equals';
  if (op === 'greater_than_or_equal') return 'greater_equal';
  if (op === 'less_than_or_equal') return 'less_equal';
  return op;
};

const toConditionRowOperator = op => {
  if (!op) return 'equals';
  if (op === 'greater_equal') return 'greater_than_or_equal';
  if (op === 'less_equal') return 'less_than_or_equal';
  return op;
};

/** Valid filters for custom-logic numbering (same idea as list view Quick Filters). */
function validReportFiltersForLogic(filters) {
  return filters.filter((f) => f.field_config_id);
}

function defaultReportCustomLogicExpression(filters) {
  const n = validReportFiltersForLogic(filters).length;
  if (n <= 0) return '1';
  if (n === 1) return '1';
  return Array.from({ length: n }, (_, i) => String(i + 1)).join(' AND ');
}

const FieldSelector = ({
  reportData,
  availableFields,
  selectedFields,
  setSelectedFields,
  selectedGroups,
  setSelectedGroups,
  filters,
  setFilters,
  activeTab,
  setActiveTab,
  filterLogic,
  setFilterLogic,
  customLogic,
  setCustomLogic,
  summarizedFields,
  setSummarizedFields,
  isEditing = false
}) => {
  const [filteredFields, setFilteredFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({
    selectedFields: false,
    groupBy: false,
    filters: false
  });
  const [allSectionsCollapsed, setAllSectionsCollapsed] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showDateHelpModal, setShowDateHelpModal] = useState(false);

  // Drag and drop states
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [draggedSection, setDraggedSection] = useState(null); // 'fields' or 'groups'

  useEffect(() => {
    console.log('FieldSelector received availableFields:', availableFields);
    console.log('FieldSelector availableFields length:', availableFields.length);
    if (availableFields.length > 0) {
      setFilteredFields(availableFields);
      console.log('FieldSelector set filteredFields to:', availableFields);
    }
  }, [availableFields]);


  const groupFieldsByObject = (fields) => {
    const grouped = {};
    fields.forEach(field => {
      const objectName = field.object_name || 'Unknown';
      if (!grouped[objectName]) {
        grouped[objectName] = {
          object_name: objectName,
          object_label: field.object_label || objectName,
          is_primary: field.is_primary_object || false,
          fields: []
        };
      }
      grouped[objectName].fields.push(field);
    });

    // Sort objects: primary first, then alphabetically
    return Object.values(grouped).sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return a.object_label.localeCompare(b.object_label);
    });
  };

  const filterFields = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredFields(availableFields);
    } else {
      const filtered = availableFields.filter(field =>
        field.field_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        field.object_label?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredFields(filtered);
    }
  };

  const handleFieldToggle = (field) => {
    const isSelected = selectedFields.find(f => f.id === field.id);
    if (isSelected) {
      setSelectedFields(selectedFields.filter(f => f.id !== field.id));
    } else {
      setSelectedFields([...selectedFields, { ...field, sortOrder: 'ASC', summaryType: 'none' }]);
    }
  };

  const handleGroupToggle = (field) => {
    const isSelected = selectedGroups.find(g => g.id === field.id);
    if (isSelected) {
      // Remove from groups
      setSelectedGroups(selectedGroups.filter(g => g.id !== field.id));
    } else {
      // Add to groups and remove from selected fields if it exists there
      setSelectedGroups([...selectedGroups, { ...field, sortOrder: 'ASC' }]);
      setSelectedFields(selectedFields.filter(f => f.id !== field.id));
    }
  };

  const isFieldSelected = (field) => {
    return selectedFields.find(f => f.id === field.id) !== undefined;
  };

  const isGroupSelected = (field) => {
    return selectedGroups.find(g => g.id === field.id) !== undefined;
  };

  const isFieldInAnySection = (field) => {
    return selectedFields.find(f => f.id === field.id) !== undefined ||
      selectedGroups.find(g => g.id === field.id) !== undefined;
  };

  const handleRemoveField = (fieldId) => {
    setSelectedFields(selectedFields.filter(f => f.id !== fieldId));
  };

  const handleSummaryTypeChange = (fieldId, summaryType) => {
    // Update the field's summaryType
    setSelectedFields(selectedFields.map(f =>
      f.id === fieldId ? { ...f, summaryType } : f
    ));

    // Update the summarizedFields state
    if (summaryType === 'none') {
      // Remove from summarized fields
      setSummarizedFields(prev => {
        const updated = { ...prev };
        delete updated[fieldId];
        return updated;
      });
    } else {
      // Add to summarized fields
      setSummarizedFields(prev => ({
        ...prev,
        [fieldId]: {
          aggregation: summaryType
        }
      }));
    }
  };

  const handleRemoveGroup = (fieldId) => {
    setSelectedGroups(selectedGroups.filter(g => g.id !== fieldId));
  };

  // Drag and drop functions for selected fields and groups
  const handleDragStart = (e, index, section) => {
    setDraggedItem(index);
    setDraggedSection(section);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index, section) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== index && draggedSection === section) {
      setDragOverItem(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, index, section) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index || draggedSection !== section) {
      setDraggedItem(null);
      setDragOverItem(null);
      setDraggedSection(null);
      return;
    }

    if (section === 'fields') {
      const newFields = [...selectedFields];
      const draggedField = newFields[draggedItem];

      // Remove the dragged item
      newFields.splice(draggedItem, 1);

      // Insert at the new position
      newFields.splice(index, 0, draggedField);

      setSelectedFields(newFields);
    } else if (section === 'groups') {
      const newGroups = [...selectedGroups];
      const draggedGroup = newGroups[draggedItem];

      // Remove the dragged item
      newGroups.splice(draggedItem, 1);

      // Insert at the new position
      newGroups.splice(index, 0, draggedGroup);

      setSelectedGroups(newGroups);
    }

    setDraggedItem(null);
    setDragOverItem(null);
    setDraggedSection(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
    setDraggedSection(null);
  };

  // Field type icon function
  const getFieldTypeIcon = (dataType) => {
    switch (dataType?.toLowerCase()) {
      case 'date':
      case 'datetime':
        return '📅';
      case 'number':
      case 'integer':
      case 'decimal':
        return '🔢';
      case 'boolean':
        return '✅';
      case 'email':
        return '📧';
      case 'phone':
        return '📞';
      case 'url':
        return '🔗';
      default:
        return '📄';
    }
  };

  // Get summary type options based on field data type
  const getSummaryTypeOptions = (dataType) => {
    const numericTypes = [
      'number',
      'integer',
      'decimal',
      'currency',
      'percent',
      'rollupsummary',
      'autonumber',
      'numeric',
      'float',
      'double',
      'real',
      'bigint',
      'smallint',
      'money'
    ];
    const dateTypes = ['date', 'datetime'];

    if (numericTypes.includes(dataType?.toLowerCase())) {
      return [
        { value: 'none', label: 'No Summary' },
        { value: 'sum', label: 'Sum' },
        { value: 'avg', label: 'Average' },
        { value: 'min', label: 'Minimum' },
        { value: 'max', label: 'Maximum' },
        { value: 'count', label: 'Count' }
      ];
    } else if (dateTypes.includes(dataType?.toLowerCase())) {
      return [
        { value: 'none', label: 'No Summary' },
        { value: 'min', label: 'Earliest' },
        { value: 'max', label: 'Latest' },
        { value: 'count', label: 'Count' }
      ];
    } else {
      return [
        { value: 'none', label: 'No Summary' },
        { value: 'count', label: 'Count' }
      ];
    }
  };

  // Filter validation functions
  const validateFilters = () => {
    const errors = [];

    // Check if all filters have fields selected
    filters.forEach((filter, index) => {
      if (!filter.field_config_id) {
        errors.push(`Filter ${index + 1}: Field must be selected`);
      }
    });

    // Check custom logic validation (aligned with list view: 1-based indices, any count ≥ 1)
    if (filterLogic === 'CUSTOM') {
      if (!customLogic || customLogic.trim() === '') {
        errors.push('Custom logic expression is required');
      } else {
        const filterNumbers = filters.map((_, index) => (index + 1).toString());
        const customLogicNumbers = customLogic.match(/\d+/g) || [];
        const missingNumbers = filterNumbers.filter(
          (num) => !customLogicNumbers.includes(num)
        );

        if (missingNumbers.length > 0) {
          errors.push(
            `Custom logic must reference each filter number (1–${filterNumbers.length}). Missing: ${missingNumbers.join(', ')}`
          );
        }
      }
    }

    return errors;
  };

  // Save filters function
  const saveFilters = () => {
    const errors = validateFilters();
    setShowValidationErrors(true);

    if (errors.length === 0) {
      // No errors, close modal
      setShowFilterModal(false);
      setShowValidationErrors(false);
    }
    // If there are errors, they will be displayed and modal stays open
  };

  // Close modal function
  const closeFilterModal = () => {
    setShowFilterModal(false);
    setShowValidationErrors(false);
  };

  // Filter management functions
  const addFilter = () => {
    const newFilter = {
      id: `filter_${Date.now()}`,
      field_config_id: '',
      field_name: '',
      field_label: '',
      object_name: '',
      operator: 'equals',
      value: '',
      logic: 'AND'
    };
    setFilters(prev => [...prev, newFilter]);
  };

  const removeFilter = (filterId) => {
    let newFilters = [];
    setFilters(prev => {
      newFilters = prev.filter(f => f.id !== filterId);
      return newFilters;
    });

    // One filter left: keep CUSTOM but normalize expression to "1"
    if (newFilters.length === 1 && filterLogic === 'CUSTOM') {
      setCustomLogic('1');
      return;
    }

    // If custom logic is selected, validate after removal
    if (filterLogic === 'CUSTOM' && customLogic) {
      const filterNumbers = newFilters.map((_, index) => (index + 1).toString());
      const customLogicNumbers = customLogic.match(/\d+/g) || [];
      const invalidNumbers = customLogicNumbers.filter(num => !filterNumbers.includes(num));

      if (invalidNumbers.length > 0) {
        // Remove invalid numbers from custom logic
        let updatedCustomLogic = customLogic;
        invalidNumbers.forEach(num => {
          updatedCustomLogic = updatedCustomLogic.replace(new RegExp(`\\b${num}\\b`, 'g'), '');
        });
        // Clean up extra spaces and operators
        updatedCustomLogic = updatedCustomLogic.replace(/\s+(AND|OR)\s+/g, ' $1 ').trim();
        setCustomLogic(updatedCustomLogic);
      }
    }
  };

  const updateFilter = (filterId, updates) => {
    setFilters(prev =>
      prev.map(f => (f.id === filterId ? { ...f, ...updates } : f))
    );
  };

  /** Same ConditionRow wiring as list view Quick Filters (ListViewFilterPanel). */
  const handleReportFilterConditionChange = filterIndex => (idx, key, val) => {
    if (idx !== filterIndex) return;

    if (key === 'field') {
      setFilters(prev => {
        const row = prev[filterIndex];
        if (!row) return prev;
        if (val === '' || val == null) {
          return prev.map((f, i) =>
            i === filterIndex
              ? {
                  ...f,
                  field_config_id: '',
                  field_name: '',
                  field_label: '',
                  object_name: '',
                  object_label: '',
                  data_type: '',
                  operator: 'equals',
                  value: '',
                }
              : f
          );
        }
        const selectedField = availableFields.find(f => String(f.id) === String(val));
        if (!selectedField) return prev;
        return prev.map((f, i) =>
          i === filterIndex
            ? {
                ...f,
                field_config_id: selectedField.id,
                field_name: selectedField.field_name,
                field_label: selectedField.field_label,
                object_name: selectedField.object_name,
                object_label: selectedField.object_label,
                data_type: selectedField.data_type,
                operator: 'equals',
                value: '',
              }
            : f
        );
      });
      return;
    }

    if (key === 'operator') {
      setFilters(prev => {
        const row = prev[filterIndex];
        if (!row) return prev;
        return prev.map((f, i) =>
          i === filterIndex ? { ...f, operator: fromConditionRowOperator(val) } : f
        );
      });
      return;
    }

    if (key === 'value') {
      setFilters(prev => {
        const row = prev[filterIndex];
        if (!row) return prev;
        return prev.map((f, i) => (i === filterIndex ? { ...f, value: val } : f));
      });
    }
  };

  const toggleGroupCollapse = (objectName) => {
    setCollapsedGroups(prev => {
      const newState = {
        ...prev,
        [objectName]: !prev[objectName]
      };

      // Check if all available field groups are collapsed
      const allGroupsCollapsed = Object.keys(newState).length === groupedFields.length &&
        Object.values(newState).every(collapsed => collapsed);

      // Update allSectionsCollapsed if all sections (including groups) are collapsed/expanded
      const allSectionsCollapsedState = allGroupsCollapsed &&
        collapsedSections.selectedFields &&
        collapsedSections.groupBy &&
        collapsedSections.filters;

      const allSectionsExpandedState = Object.keys(newState).length === 0 &&
        !collapsedSections.selectedFields &&
        !collapsedSections.groupBy &&
        !collapsedSections.filters;

      if (allSectionsCollapsedState) {
        setAllSectionsCollapsed(true);
      } else if (allSectionsExpandedState) {
        setAllSectionsCollapsed(false);
      }

      return newState;
    });
  };

  const toggleSectionCollapse = (sectionName) => {
    setCollapsedSections(prev => {
      const newState = {
        ...prev,
        [sectionName]: !prev[sectionName]
      };

      // Check if all sections are now collapsed or expanded
      const allCollapsed = newState.selectedFields && newState.groupBy && newState.filters;
      const allExpanded = !newState.selectedFields && !newState.groupBy && !newState.filters;

      if (allCollapsed) {
        setAllSectionsCollapsed(true);
      } else if (allExpanded) {
        setAllSectionsCollapsed(false);
      }
      // If mixed state, don't change allSectionsCollapsed

      return newState;
    });
  };

  const toggleAllSections = () => {
    if (allSectionsCollapsed) {
      // Expand all sections
      setCollapsedGroups({});
      setCollapsedSections({
        selectedFields: false,
        groupBy: false,
        filters: false
      });
      setAllSectionsCollapsed(false);
    } else {
      // Collapse all sections
      const allCollapsed = {};
      groupedFields.forEach(group => {
        allCollapsed[group.object_name] = true;
      });
      setCollapsedGroups(allCollapsed);
      setCollapsedSections({
        selectedFields: true,
        groupBy: true,
        filters: true
      });
      setAllSectionsCollapsed(true);
    }
  };

  const groupedFields = groupFieldsByObject(filteredFields);

  return (
    <div className="field-selector field-selector-drag-drop">
      <div className="field-selector-header">
        <div className="header-top">
          <h4>
            Field Selector
            {isEditing && <span className="edit-mode-indicator"> (Edit Mode)</span>}
          </h4>
          <div className="header-actions">
            <button
              type="button"
              className="action-btn"
              onClick={toggleAllSections}
              title={allSectionsCollapsed ? "Expand All Sections" : "Collapse All Sections"}
            >
              <FaChevronDown className={allSectionsCollapsed ? "collapsed" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Selected Fields Summary */}
      <div className="field-group">
        <div
          className="group-header primary"
          onClick={() => toggleSectionCollapse('selectedFields')}
        >
          <div className="group-header-content">
            <h5>Selected Fields</h5>
            <span className="field-count">({selectedFields.length} fields)</span>
          </div>
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 448 512"
            className={`collapse-icon ${collapsedSections.selectedFields ? 'collapsed' : ''}`}
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
          </svg>
        </div>
        {selectedFields.length > 0 && !collapsedSections.selectedFields && (
          <div className="field-list">
            {selectedFields.map((field, index) => (
              <div
                key={field.id}
                className="field-item"
                draggable={true}
                onDragStart={(e) => handleDragStart(e, index, 'fields')}
                onDragOver={(e) => handleDragOver(e, index, 'fields')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index, 'fields')}
                onDragEnd={handleDragEnd}
                style={{
                  opacity: draggedItem === index ? 0.5 : 1,
                  transform: draggedItem === index ? 'rotate(2deg)' : 'none',
                  border: dragOverItem === index ? '2px solid #3b82f6' : '1px solid #e1e5e9',
                  background: dragOverItem === index ? '#f0f7ff' : 'white'
                }}
              >
                <div className="field-checkbox">
                  <span className="field-icon">{getFieldTypeIcon(field.data_type)}</span>
                  <label>
                    {field.field_label}
                    <small className="field-name">
                      ({field.field_name})
                      {field.summaryType && field.summaryType !== 'none' && (
                        <span style={{
                          color: '#3b82f6',
                          fontWeight: '600',
                          marginLeft: '8px'
                        }}>
                          • {getSummaryTypeOptions(field.data_type).find(opt => opt.value === field.summaryType)?.label}
                        </span>
                      )}
                    </small>
                  </label>
                </div>
                <div className="summary-selector" style={{ marginRight: '8px' }}>
                  <select
                    value={field.summaryType || 'none'}
                    onChange={(e) => handleSummaryTypeChange(field.id, e.target.value)}
                    style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      border: '1px solid #e1e5e9',
                      borderRadius: '4px',
                      background: 'white',
                      color: '#374151'
                    }}
                  >
                    {getSummaryTypeOptions(field.data_type).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field-actions">
                  <div
                    className="drag-handle"
                    style={{
                      color: '#6c757d',
                      cursor: 'grab',
                      fontSize: '14px',
                      padding: '6px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '8px'
                    }}
                  >
                    <FaGripVertical />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveField(field.id)}
                    className="remove-btn"
                    title="Remove Field"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedFields.length === 0 && (
          <div className="field-list">
            <div className="empty-selected-fields">
              <div className="empty-icon">📋</div>
              <p>No fields selected</p>
              <small>Select fields from the available fields below</small>
            </div>
          </div>
        )}
      </div>

      {/* Selected Groups Summary */}
      <div className="field-group">
        <div
          className="group-header primary"
          onClick={() => toggleSectionCollapse('groupBy')}
        >
          <div className="group-header-content">
            <h5>Group By</h5>
            <span className="field-count">({selectedGroups.length} fields)</span>
          </div>
          <svg
            stroke="currentColor"
            fill="currentColor"
            strokeWidth="0"
            viewBox="0 0 448 512"
            className={`collapse-icon ${collapsedSections.groupBy ? 'collapsed' : ''}`}
            height="1em"
            width="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
          </svg>
        </div>
        {selectedGroups.length > 0 && !collapsedSections.groupBy && (
          <div className="field-list">
            {selectedGroups.map((group, index) => (
              <div
                key={group.id}
                className="field-item"
                draggable={true}
                onDragStart={(e) => handleDragStart(e, index, 'groups')}
                onDragOver={(e) => handleDragOver(e, index, 'groups')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index, 'groups')}
                onDragEnd={handleDragEnd}
                style={{
                  opacity: draggedItem === index ? 0.5 : 1,
                  transform: draggedItem === index ? 'rotate(2deg)' : 'none',
                  border: dragOverItem === index ? '2px solid #3b82f6' : '1px solid #e1e5e9',
                  background: dragOverItem === index ? '#f0f7ff' : 'white'
                }}
              >
                <div className="field-checkbox">
                  <span className="field-icon">🔢</span>
                  <label>
                    {group.field_label}
                    <small className="field-name">({group.field_name})</small>
                  </label>
                </div>
                <div className="field-actions">
                  <div
                    className="drag-handle"
                    style={{
                      color: '#6c757d',
                      cursor: 'grab',
                      fontSize: '14px',
                      padding: '6px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '8px'
                    }}
                  >
                    <FaGripVertical />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveGroup(group.id)}
                    className="remove-btn"
                    title="Remove Group"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedGroups.length === 0 && (
          <div className="field-list">
            <div className="empty-selected-fields">
              <div className="empty-icon">📊</div>
              <p>No groups selected</p>
              <small>Select fields for grouping from the available fields below</small>
            </div>
          </div>
        )}
      </div>

      {/* Filters Section */}
      <div className="field-group">
        <div
          className="group-header primary"
          onClick={() => toggleSectionCollapse('filters')}
        >
          <div className="group-header-content">
            <h5>Filters</h5>
            <span className="field-count">({filters.length} filters)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowFilterModal(true);
              }}
              className="add-filter-btn"
              title="Add Filter"
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <FaPlus />
              Add Filter
            </button>
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 448 512"
              className={`collapse-icon ${collapsedSections.filters ? 'collapsed' : ''}`}
              height="1em"
              width="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
            </svg>
          </div>
        </div>
        {filters.length > 0 && !collapsedSections.filters && (
          <div className="field-list">
            {filters.length > 1 && (
              <div className="filter-logic-display" style={{
                padding: '8px 12px',
                background: '#e3f2fd',
                border: '1px solid #bbdefb',
                borderRadius: '4px',
                marginBottom: '8px',
                fontSize: '12px',
                color: '#1976d2',
                fontWeight: '500'
              }}>
                <span className="filter-logic-text">
                  Filter Logic: {filterLogic}
                  {filterLogic === 'CUSTOM' && customLogic && ` [${customLogic}]`}
                </span>
              </div>
            )}
            {filters.map((filter, index) => (
              <div key={filter.id} className="field-item" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                marginBottom: '4px',
                background: 'white',
                border: '1px solid #e1e5e9',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}>
                <div className="field-checkbox">
                  <span className="field-icon">🔍</span>
                  <label>
                    <span className="filter-field-name">
                      {filter.object_name && filter.object_label
                        ? `${filter.object_label}: ${filter.field_label || 'Select Field'}`
                        : filter.field_label || 'Select Field'
                      }
                    </span>
                    <small className="field-name">
                      {filter.operator} {filter.value && `"${filter.value}"`}
                      {filter.is_editable !== false ? ' (Editable)' : ' (Not Editable)'}
                    </small>
                  </label>
                </div>
                <div className="field-actions">
                  <button
                    type="button"
                    onClick={() => removeFilter(filter.id)}
                    className="remove-btn"
                    title="Remove Filter"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      padding: '6px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {filters.length === 0 && (
          <div className="field-list">
            <div className="empty-selected-fields">
              <div className="empty-icon">🔍</div>
              <p>No filters configured</p>
              <small>Click "Add Filter" to create filtering rules</small>
            </div>
          </div>
        )}
      </div>

      <div className="field-groups">
        <h5 className="available-fields-title">Available Fields</h5>
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              filterFields(e.target.value);
            }}
            className="search-input"
          />
        </div>
        {groupedFields.map(objectGroup => (
          <div key={objectGroup.object_name} className="field-group">
            <div
              className={`group-header ${objectGroup.is_primary ? 'primary' : ''}`}
              onClick={() => toggleGroupCollapse(objectGroup.object_name)}
            >
              <div className="group-header-content">
                <h5>
                  {objectGroup.object_label}
                  {objectGroup.is_primary && ' (Primary)'}
                </h5>
                <span className="field-count">({objectGroup.fields.length} fields)</span>
              </div>
              <FaChevronDown
                className={`collapse-icon ${collapsedGroups[objectGroup.object_name] ? 'collapsed' : ''}`}
              />
            </div>
            {!collapsedGroups[objectGroup.object_name] && (
              <div className="field-list">
                {objectGroup.fields.map(field => (
                  <div key={field.id} className="field-item">
                    <div className="field-checkbox">
                      <input
                        type="checkbox"
                        id={`field-${field.id}`}
                        checked={isFieldInAnySection(field)}
                        onChange={() => handleFieldToggle(field)}
                      />
                      <label htmlFor={`field-${field.id}`}>
                        {field.field_label}
                      </label>
                    </div>
                    <div className="field-actions">
                      <button
                        type="button"
                        className={`group-btn ${isGroupSelected(field) ? 'selected' : ''}`}
                        onClick={() => handleGroupToggle(field)}
                        title={isGroupSelected(field) ? "Remove from Group By" : "Add to Group By (Record count will be displayed automatically)"}
                      >
                        <FaFilter />
                      </button>
                      {isGroupSelected(field) && (
                        <div className="group-indicator" title="Record count will be displayed automatically">
                          <span className="indicator-text">Count</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Filter Management Modal */}
      {showFilterModal && (
        <div className="filter-modal-overlay">
          <div className="filter-modal">
            <div className="filter-modal-header">
              <h3>Manage Filters</h3>
              <button
                type="button"
                onClick={() => setShowFilterModal(false)}
                className="close-modal-btn"
              >
                <FaTimes />
              </button>
            </div>

            <div className="filter-modal-content">
              {/* Validation Errors Display */}
              {showValidationErrors && (() => {
                const errors = validateFilters();
                return errors.length > 0 && (
                  <div className="filter-validation-errors">
                    <h4>Validation Errors:</h4>
                    <ul>
                      {errors.map((error, index) => (
                        <li key={index} className="validation-error">{error}</li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              <div className="filter-modal-actions">
                <button
                  type="button"
                  onClick={addFilter}
                  className="add-filter-btn"
                >
                  <FaPlus /> Add Filter
                </button>
              </div>

              <div className="filters-table-container">
                <table className="filters-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Field, operator &amp; value</th>
                      <th>Edit at run</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filters.map((filter, index) => {
                      const selectedField =
                        filter.field_config_id !== '' && filter.field_config_id != null
                          ? availableFields.find(
                              f => String(f.id) === String(filter.field_config_id)
                            ) || null
                          : null;
                      const dt = (selectedField?.data_type || '').toLowerCase();
                      const showDateHint =
                        selectedField &&
                        (dt === 'date' || dt === 'datetime') &&
                        isOperatorValueRequired(toConditionRowOperator(filter.operator));

                      return (
                        <tr key={filter.id} className="filter-row">
                          <td className="filter-number">{index + 1}</td>
                          <td>
                            <ConditionRow
                              condition={{
                                field: filter.field_config_id ? String(filter.field_config_id) : '',
                                operator: toConditionRowOperator(filter.operator || 'equals'),
                                value: filter.value ?? '',
                              }}
                              fields={availableFields}
                              onChange={handleReportFilterConditionChange(index)}
                              index={index}
                              options={{
                                fieldKey: 'field',
                                nameKey: 'id',
                                labelKey: 'field_display_name',
                                /** Value is field UUID; hide subtitle so the list is not a wall of IDs */
                                hideSubtitle: true,
                                fieldColSize: 4,
                                operatorColSize: 3,
                                valueColSize: 5,
                                useSearchableDropdown: true,
                              }}
                            />
                            {showDateHint && (
                              <div className="small text-muted mt-1 d-flex align-items-center gap-1">
                                <FaQuestionCircle
                                  className="text-primary"
                                  size={12}
                                  style={{ cursor: 'pointer' }}
                                  title="Date filter help"
                                  onClick={() => setShowDateHelpModal(true)}
                                />
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="text-primary"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setShowDateHelpModal(true)}
                                  onKeyDown={e => e.key === 'Enter' && setShowDateHelpModal(true)}
                                >
                                  Date / datetime literals and formats
                                </span>
                              </div>
                            )}
                          </td>
                          <td>
                            <label className="edit-filter-checkbox">
                              <input
                                type="checkbox"
                                checked={filter.is_editable !== false}
                                onChange={e =>
                                  updateFilter(filter.id, { is_editable: e.target.checked })
                                }
                              />
                              <span className="checkbox-label">Editable</span>
                            </label>
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeFilter(filter.id)}
                              className="remove-filter-btn"
                              title="Remove Filter"
                            >
                              <FaTimes />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Filter Logic Selector — same behavior as list view Quick Filters */}
              {filters.length > 0 && (
                <div className="filter-logic-selector">
                  <label className="logic-label">Filter Logic:</label>
                  <select
                    value={filterLogic}
                    onChange={(e) => {
                      const newLogic = e.target.value;
                      setFilterLogic(newLogic);
                      if (newLogic === 'CUSTOM') {
                        setCustomLogic((prev) => {
                          const t = (prev || '').trim();
                          if (t) return prev;
                          return defaultReportCustomLogicExpression(filters);
                        });
                      } else {
                        setCustomLogic('');
                      }
                    }}
                    className="logic-dropdown"
                  >
                    <option value="AND">AND (All conditions must be true)</option>
                    <option value="OR">OR (Any condition can be true)</option>
                    <option value="CUSTOM">Custom (e.g. (1 OR 2) AND 3)</option>
                  </select>
                  {filterLogic === 'CUSTOM' && (
                    <div className="custom-logic-input">
                      <label>Custom expression</label>
                      <textarea
                        value={customLogic}
                        onChange={(e) => setCustomLogic(e.target.value)}
                        placeholder="(1 OR 2) AND 3"
                        className="custom-logic-text"
                        rows={2}
                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      />
                      <div className="logic-help">
                        <small>
                          Use numbers 1, 2, 3… for each filter row above, with AND, OR, and parentheses.
                          Same rules as list view filters.
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="filter-modal-footer">
              <button
                type="button"
                onClick={closeFilterModal}
                className="btn btn-secondary"
              >
                Close
              </button>
              <button
                type="button"
                onClick={saveFilters}
                className="btn btn-primary"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <DateFilterLiteralsHelpModal
        show={showDateHelpModal}
        onHide={() => setShowDateHelpModal(false)}
      />
    </div>
  );
};

export default FieldSelector;
